import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import moment from 'moment';
import { DisplayDocumentComponent, HANDLED_COURRIER, HANDLED_HTML_CONTENT, HANDLED_IMAGE_MIME_TYPES, HANDLED_PDF_MIM_TYPES, HANDLED_FROALA_CONTENT } from 'src/app/fragments/display-document/display-document.component';
import { DisplayContentData } from 'src/app/fragments/display-document/gedviewer/gedviewer.component';
import { Courrier } from 'src/app/utils/interfaces/new/Courrier';
import { DocumentMetadata } from 'src/app/utils/interfaces/new/DocumentMetadata';
import { QuillObjectDisplay } from 'src/app/utils/interfaces/new/quill-object-display.interface';
import { DocumentService } from '../document/document.service';
import { HeaderFooterService } from '../header-footer/header-footer.service';
import { LoginService } from '../login/login.service';
import { ModalService } from '../modal/modal.service';
import { PatientService } from '../patient/patient.service';
import { OrdonnanceNumeriqueService, SousTypeOrdonnanceNumerique } from '../ordonnance-numerique/ordonnance-numerique.service';
import { InfoEprescription } from 'src/app/fragments/modal/envoi-eprescription/envoi-eprescription.fragment';
import { Ordonnance } from 'src/app/utils/interfaces/new/ordonnance';
import { RessourceService , enTeteVideHauteur} from '../ressource/ressource.service';
import { StringUtilsService } from '../string-utils/string-utils.service';
import { nomSettingKey, prenomSettingKey } from '../dmp/dmp.service';
import { QuillUtilsService } from '../quill-utils/quill-utils.service';
import { Correspondant } from 'src/app/utils/interfaces/new/correspondant.interface';
import { SnackbarService } from '../snackbar/snackbar.service';
import { Annexe } from 'src/app/fragments/modal/edit-courrier/edit-courrier.fragment';
import { SafeHtmlUtils } from '../../utils/safe-html.util';

@Injectable({
  providedIn: 'root'
})
export class DisplayDocumentService {

  constructor(
    private _modalSrv: ModalService,
    private _headFootSrv: HeaderFooterService,
    private _patientSrv: PatientService,
    private _loginSrv: LoginService,
    private _documentSrv: DocumentService,
    private _resourceSrv: RessourceService,
    private _strUtilsSrv: StringUtilsService,
    private _snackBarSrv: SnackbarService
  ) { }

  /**
   * Cette fonction permet de traiter l'affichage d'un document/fichier/courrier.
   */
  public async displayDocument(
    viewContainerRef: ViewContainerRef,
    document: DisplayContentData,
    printDirectly?: boolean
  ): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      try {
        if (HANDLED_IMAGE_MIME_TYPES.includes(document.contentType)
          || HANDLED_PDF_MIM_TYPES.includes(document.contentType)
          || HANDLED_FROALA_CONTENT.includes(document.contentType)
          || HANDLED_COURRIER.includes(document.contentType)) {
          const modal = this._modalSrv.openModal<DisplayDocumentComponent>(
            viewContainerRef, DisplayDocumentComponent
          ) as ComponentRef<DisplayDocumentComponent>;
          modal.instance.parentViewContainerRef = viewContainerRef;
          if(document.contentType === 'ordonnance-consultation' && printDirectly === true){
          modal.instance.printDirectly = printDirectly
            }
          if (!document.data) {
            modal.instance.document = document;
            const doc = await this._documentSrv.downloadDocument(document.id);
            document.data = DocumentService.prefixBase64(doc.payload, doc.contentType);
          } else {
            modal.instance.document = document;
          }

          modal.instance.closeEmitter.subscribe(() => {
            modal.destroy();
          });
          resolve(modal);
        } else if (HANDLED_HTML_CONTENT.includes(document.contentType)) {
          const modal = this._modalSrv.openModal<DisplayDocumentComponent>(
            viewContainerRef, DisplayDocumentComponent
          ) as ComponentRef<DisplayDocumentComponent>;
          modal.instance.parentViewContainerRef = viewContainerRef;
          document.data = DocumentService.prefixBase64(document.data, document.contentType);
          modal.instance.document = document;

          modal.instance.closeEmitter.subscribe(() => {
            modal.destroy();
          });
          resolve(modal);
        } else {
          const modal = this._modalSrv.openModalConfirmation(
            viewContainerRef,
            'Affichage impossible',
            'Nous ne pouvons pas ouvrir ce fichier.\n\nVoulez-vous le télécharger ?'
          );
          modal.instance.closeEmitter.subscribe(async (res: boolean) => {
            if (res) {
              const doc = await this._documentSrv.downloadDocument(document.id);
              document.data = DocumentService.prefixBase64(doc.payload, doc.contentType);
              fetch(document.data)
                .then(resp => resp.blob())
                .then(blob => {
                  const url = window.URL.createObjectURL(blob);
                  const a = window.document.createElement('a');
                  a.style.display = 'none';
                  a.href = url;
                  a.download = document.fileName;
                  window.document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  window.document.body.removeChild(a);
                });
            }
          });
          resolve(undefined);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  public canDisplayDocument(contentType: string): boolean {
    return DisplayDocumentComponent.prototype.canDisplayDocument(contentType);
  }

  public documentToDisplayContentData(document: DocumentMetadata, patientId?: string): DisplayContentData {
    return DisplayDocumentComponent.prototype.documentToDisplayContentData(document, patientId);
  }

  public async courrierToDisplayContentData(courrier: Courrier, patientId: string, consultationId?: string, correspondants?: Correspondant[], doctorId?:string, annexes?: Annexe[]): Promise<DisplayContentData> {
    return DisplayDocumentComponent.prototype
      .courrierToDisplayContentData(courrier, await this._openCourrier(courrier, patientId, correspondants, doctorId), patientId, consultationId, annexes);
  }

  public async quillToDisplayContentData(quillObject: QuillObjectDisplay): Promise<DisplayContentData> {
    return DisplayDocumentComponent.prototype.QuillToDisplayContentData(quillObject);
  }

  public async htmlToDisplayContentData( object: QuillObjectDisplay ): Promise<DisplayContentData> {
    return DisplayDocumentComponent.prototype.HtmlToDisplayContentData( object );
  }



  private async _openCourrier(courrier: any, patientId: string, correspondants?: Correspondant[], doctorId?:string): Promise<CourrierContent> {
    const enteteVide = courrier.isEnteteVide ? true : false;
    const piedVide = courrier.isPiedVide ? true : false;
    const duplicata = courrier.isDuplicata ? true : false;
    const forPatient = courrier.isImpressionPatient ? true : false;
    const villeDate = courrier.isVilleDate ? true : false;
    const nomPatient = courrier.isNomPatient ? true : false;
    let rawHeader;
    let rawFooter;

    if (!courrier.isOrdo && !courrier.isOrdoHisto) {
      courrier.courrierText = SafeHtmlUtils.sanitizeHtmlString(courrier.courrierText, true);
    }

    if(doctorId){
      rawHeader = await this._headFootSrv.findOneHeader(doctorId);
    }else{
      rawHeader = await this._headFootSrv.findOneHeader();
    }

    if(doctorId){
      rawFooter = await this._headFootSrv.findOneFooter(doctorId);
    }else{
      rawFooter = await this._headFootSrv.findOneFooter();
    }
    if(courrier.isOrdo){ // on vérifie si c'est une ordonnance si oui on cherche le nbproduits cacher
      const match = courrier.courrierText.match(/id="nbproduits-(\d+)"/);
      const nbProduits = match ? parseInt(match[1], 10) : 0;
        const texteBloc = `
        <p style="text-align: right;">
          <span style=" display: inline-block; width: 40px; height: 40px; line-height: 40px;
            font-size: 18px; font-weight: bold; text-align: center; vertical-align: middle;
            border: 1px solid black; outline: 1px solid black; outline-offset: -4px;">${nbProduits > 0 ? `${nbProduits}` : '&nbsp;&nbsp;'}</span>
        </p>
      `;
      if ( rawFooter && typeof rawFooter.texte === 'string' && typeof rawFooter.footerHeight === 'number') {
        rawFooter.texte = texteBloc + rawFooter.texte;
        rawFooter.footerHeight += 40; // ajuste si nécessaire
      }
    }

    let beforePrintContents = '';
    let afterPrintContents = '';
    let firstPageOnly = rawHeader?.firstPageOnly ? rawHeader.firstPageOnly : false;
    let header='';
    let footer='';
    let headerHeight = rawHeader?.headerHeight ? rawHeader.headerHeight : null;
    let footerHeight = rawFooter?.footerHeight ? rawFooter.footerHeight : null;


    if (!enteteVide && rawHeader && rawHeader.texte) {
      header = SafeHtmlUtils.sanitizeHtmlString(rawHeader.texte, true);
    } else {
      header = '';
    }

    if (villeDate) {
      const currentProfil = this._loginSrv.getCurrentProfil();
      if (currentProfil.cabinet) {
        beforePrintContents += '<p style="margin-top:10px;">A ' + SafeHtmlUtils.escapeHtml(currentProfil.cabinet.ville) +' le '+ moment().format('DD/MM/YYYY') +'</p>';
      } else {
        beforePrintContents += '<p>Le : ' + moment().format('DD/MM/YYYY') + '</p>';
      }
    }

    if (nomPatient) {
      const fichePatient = await this._patientSrv.findOnePatientFiche(patientId);
      if (fichePatient) {
        beforePrintContents += '<p>Nom et prénom du patient : ' + SafeHtmlUtils.escapeHtml(fichePatient.nom) +' '+ SafeHtmlUtils.escapeHtml(fichePatient.prenom?fichePatient.prenom:'') + '</p>';
      }
    }

    if (!piedVide) {
      if (rawFooter && rawFooter.texte) {
        footer = SafeHtmlUtils.sanitizeHtmlString(rawFooter.texte, true);
      } else {
        footer = '';
      }
      afterPrintContents += footer;
    }

    const contents = beforePrintContents + courrier.courrierText + '<br><br>' + afterPrintContents;
    const contentHistoOrdo = courrier.courrierText;
    let rtd: CourrierContent;
    if (courrier.isOrdoHisto) { //Impression des ordonnances historisées dans "Documents patient"
        // Hack pour trouver ePrescription
        if (courrier.courrierText && courrier.courrierText.includes('qrCodeEprescription')) {
          const eprescriptionContent = courrier.courrierText.replace('<div class="title-text">',
            '<div class="title-text">' + beforePrintContents);
          rtd = {
            fullContent: eprescriptionContent,
            quillLessContent: eprescriptionContent,
            printContent: eprescriptionContent,
            isPagedActive: true
          };
          return rtd;
        } else {
          rtd = {
            fullContent: contentHistoOrdo,
            quillLessContent: contentHistoOrdo,
            printContent: this. _generateDuplicataOrdoContent(contentHistoOrdo)
         };
        }
    }
    else if (courrier.ordonnance && courrier.ordonnance.isEprescription) { //pas d'option de duplicata dans l'interface
      rtd = {
        fullContent: await this._generateContentToPrintEprescription(courrier.ordonnance,
          courrier.courrierText, header, courrier.titre, courrier.ePrescriptionInfo
        ),
        quillLessContent: contents,
        printContent: await this._generateContentToPrintEprescription(courrier.ordonnance,
          courrier.courrierText, header, courrier.titre, courrier.ePrescriptionInfo
        ),
      }
    }
    else { //gestion du pavé correspondant
      let correspondantsToPrint: string[] = [];

      if (correspondants && correspondants.length > 0) {
        for (const correspondant of correspondants) {
          correspondantsToPrint.push(this._generateCorrespondantHtml(correspondant))
        }
        rtd = {
          fullContent: await this._generateMultiplePageContentToPrint(
            courrier.courrierText,firstPageOnly, beforePrintContents,enteteVide,header,headerHeight, afterPrintContents, footerHeight, correspondantsToPrint, { forPatient, duplicata }, courrier.titre
          ),
          quillLessContent: contents,
          printContent: await this._generateMultiplePageContentToPrint(
            courrier.courrierText,firstPageOnly, beforePrintContents,enteteVide,header,headerHeight, afterPrintContents, footerHeight, correspondantsToPrint, { forPatient, duplicata }, courrier.titre
          ),
          isPagedActive:false,
        };

        correspondantsToPrint = [];


      } else {
        rtd = {
          fullContent: this._generateContent(
            courrier.courrierText, beforePrintContents,header, afterPrintContents, { forPatient, duplicata }, courrier.titre
          ),
          quillLessContent: contents,
          printContent: await this._generateContentToPrint(
            courrier.courrierText,firstPageOnly, beforePrintContents,enteteVide,header, headerHeight, afterPrintContents, footerHeight, { forPatient, duplicata }, courrier.titre
          ),
          isPagedActive:false,

        };
      }


    }
    return rtd;
  }

//génère le pavé correspondant
  private _generateCorrespondantHtml(correspondant: Correspondant): string {
    return `<div style="width:100%; place-items:right; display:flex; justify-content:flex-end; margin: 2rem 0; "><div style="display:flex;justify-content:flex-end; text-align:right;flex-wrap:wrap; margin-right:40px; width:350px;">
    ${correspondant.nom ? correspondant.nom : ''}<br>
    ${correspondant.adresse1 ? correspondant.adresse1 : ''}${correspondant.adresse2 ? '<br>'+correspondant.adresse2 : ''}
    ${correspondant.codePostal ? '<br>'+correspondant.codePostal : ''} ${correspondant.ville ? correspondant.ville : ''}
    </div></div>`
  }

  private async _generateContentToPrintEprescription(ordonnance: Ordonnance, content: any, header: string, title: any, ePrescriptionInfo: InfoEprescription): Promise<string> {
    let base64QrCode = OrdonnanceNumeriqueService.placeholderQRBase64;
    let numeroEprescription = 'XXXXXXXXXXXXXXXXXXX';
    let consentement = 'Non récupéré pour le moment';
    if (ePrescriptionInfo) {
      base64QrCode = ePrescriptionInfo.qrCodeBase64;
      consentement = ePrescriptionInfo.consentement ? 'Oui' : 'Non';
      numeroEprescription = ePrescriptionInfo.numeroEprescription;
    }

    let titleText = 'Ordonnances de médicaments, de produits ou de prestations';
    let phraseMex = '';

    // Vérification phrase MEX
    if (ordonnance.sousTypeEprescription && ordonnance.sousTypeEprescription == SousTypeOrdonnanceNumerique.MEX) {
      // Récupération du nom prénom du docteur
      let prenom = '';
      let nom = '';
      const currentProfilStr = localStorage.getItem('currentProfil');
      if (currentProfilStr && currentProfilStr !== '') {
        const currentProfil = JSON.parse(currentProfilStr);
        const params = await this._resourceSrv.getResourceSettings(currentProfil.id, 'profil');
        if (params) {
          const nomSetting = params.find(p => p.settingKey === nomSettingKey);
          if (nomSetting) {
            nom = nomSetting.settingValue;
          }
          const prenomSetting = params.find(p => p.settingKey === prenomSettingKey);
          if (prenomSetting) {
            prenom = this._strUtilsSrv.uppercaseFirstCharString(prenomSetting.settingValue);
          }
        }
      }
      titleText = "Ordonnances de médicaments, de produits ou de prestations d'exception"
      phraseMex = "Je soussigné(e), Docteur " + prenom + " " + nom + ", atteste que la" +
        " prescription concernant le patient susvisé est conforme aux indications et aux" +
        " conditions des prescription et d'utilisation prévues par la fiche d'information" +
        " thérapeutique établie par la Haute Autorité de Santé. S'il existe, le volet patient de" +
        " ladite fiche a été remis par mes soins à ce patient.";
    }

    const customStyle = `
    body {
      line-height: 1.5;
      font-size: 13px;
    }

    p, h4, h5 {
      margin: 0;
      padding: 0;
    }

    .footer {
      display: none;
      width: 100%;
      position: running(footer);
    }

    .content {
      height: auto;
      display: block;
    }

    .footer-content {
      display: flex;
      align-items: start;
    }

    .footer-content p {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
    }

    b {
      font-weight: bold;
    }

    .ql-editor {
      box-sizing: inherit !important;
      line-height: inherit !important;
      height: inherit !important;
      outline: inherit !important;
      overflow-y: inherit !important;
      padding: inherit !important;
      tab-size: inherit !important;
      -moz-tab-size: inherit !important;
      text-align: inherit !important;
      white-space: inherit !important;
      word-wrap: inherit !important;
    }

    .qrCodeEprescription {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex: 0 0 auto;
      font-size: 12px
      font-family: Arial, Helvetica, sans-serif;
    }

    .consentement {
      flex: 1 1 auto;
      padding-left: 20px;
      text-align: left;
    }

    .line-container {
      page-break-inside: avoid;
    }

    .title-text {
      font-size: 18px;
      font-weight: bold;
      text-align: center;
      margin-bottom: 10px;
    }

    .header {
      margin-bottom: 10px;
    }

    .mex {
      font-size: 11px;
    }

    @page {
      size: A4;
      margin: 1cm;
      margin-bottom: 6cm;
      @bottom-center {
        content: element(footer);
      }
    }
    `;

    let html = `
      <html>
        <head>
          <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
          <style>
          ${QuillUtilsService.setQuillPreviewStyle(customStyle)}
          </style>
           <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
          <link href='https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css' rel='stylesheet'>
          <link href='https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css' rel='stylesheet'>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
          <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>
        </head>
        <body class="fr-view">`
    if (!ePrescriptionInfo || ePrescriptionInfo.isSentOk) {
      html += `
          <!-- MUST BE FIRST FOR position: running and content: element -->
          <div class="footer">
            <div class="footer-content">
              <div class="qrCodeEprescription">
                  <img src="${base64QrCode}" alt="QR Code">
                  <div>
                    e-prescription<br />
                    N°${numeroEprescription}
                  </div>
              </div>
              <div class="consentement">
                <p>Le patient ou le ou les titulaire(s) de l'autorité parentale a (ont) accepté que je puisse consulter ce qui a
                été délivré ou exécuté sur la présente prescription : <b>${consentement}</b></p>
                <p>Les données de la prescription sont transmises électroniquement à l'assurance maladie qui traite vos
                données dans le cadre de ses missions. Pour en savoir plus sur la gestion de vos données personnelles
                et pour exercer vos droits, reportez-vous à https://www.ameli.fr</p>
              </div>
            </div>
          </div>`;
    }
    html += `
          <div>
            <div class="content ql-editor">
              <div class="header">${header}</div>
              <div class="title-text">${titleText}</div>
              <div class="mex">${phraseMex}</div>
              ${content}
            </div>
          </div>
        </body>
      </html>
    `;
    return html;
  }

  private _footerStylizationForPrint(footer: string): string {
    const div = document.createElement('div');
    div.style.position = "fixed";
    div.style.bottom = "0px";
    div.style.width = "100%";
    div.style.fontSize = "11px";
    div.style.lineHeight="1";
    div.innerHTML = footer.trim();
    return div.outerHTML;
  }

  private _generateDuplicataOrdoContent(content: string): string {

    const printStyles = `
    <style>
      @page {
        size: A4 !important;
      }

      .ql-editor {
        overflow-y: visible !important;
      }

      @media print {
        body::before {
          content: "DUPLICATA";
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-25deg);
          font-size: 42px;
          font-weight: 700;
          color: rgba(200,0,0,0.2);
          pointer-events: none;
          z-index: 9999;
        }
      }
    </style>
    `;

    if (/<head[^>]*>/i.test(content)) {
      return content.replace(/<head[^>]*>/i, match => `${match}${printStyles}`);
    }

    return `${printStyles}${content}`;
  }

  private _generateContent(content: string,beforePrintContents: string, header: string, footer: string, options: { forPatient: boolean, duplicata: boolean }, title?: string): string {
    const pageBreakElement = `<div style="page-break-after: always;"></div>`;
    let beforeBlock = '';
    if(beforePrintContents){
      beforeBlock = `</br><div style="text-align:left;">${beforePrintContents}</div><br></br>`;
    }
    let paginatedContent = `
      <div>${header}</div>
      </br>
          ${beforeBlock}
      <div>${content}</div>
      <br></br>
      <div style="text-align:center;" class="ql-align-center">${footer}</div>
    `;

    if (options.forPatient) {
      paginatedContent += `
          ${pageBreakElement}
          <hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;">
          <div>${header}</div>
          <br></br>
          <h1 class="ql-align-center">DUPLICATA PATIENT</h1>
          </br>
          ${beforeBlock}
          <div>${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
      `;
    }

    if (options.duplicata) {
      paginatedContent += `
          ${pageBreakElement}
          <hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;">
          <div>${header}</div>
          <br></br>
          <h1 class="ql-align-center">DUPLICATA</h1>
          </br>
          ${beforeBlock}
          <div>${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
      `;
    }

    return `
      <html style="height: 100%;">
        <head>
          <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
          <style>
          ${QuillUtilsService.setQuillPreviewStyle(QuillUtilsService.resetStyle)}
          </style>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
        </head>
        <body style="height: 100%; margin: 0 13px;" >
          <div id="editor" class="ql-container ql-bubble">
            <div style="height: 100%; display: flex; flex-direction: column; " class="ql-editor fr-view">
              ${paginatedContent}
            </div>
          </div>
        </body>
      </html>
    `;
  }

//génère une nouvelle page par correspondant et un duplicata par correspondant
  private _generateMultipleContent(content: string,beforePrintContents:string, header:string, footer: string, correspondantsToPrint: string[], options: { forPatient: boolean, duplicata: boolean }, title?: string): string {

    let paginatedContent = ''
    const pageBreakElement = `<div style="page-break-after: always;"></div>`
    for (let i = 0; i < correspondantsToPrint.length; i++) {
      if (correspondantsToPrint.length === 1) {
          paginatedContent += `
          <div>${header}</div>
          <br></br>
          <div style="text-align:left;">${beforePrintContents}</div>
          <div style="margin-top:3rem;">${correspondantsToPrint[i]}${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
        `;
        if (options.forPatient) {
          paginatedContent += `
          ${pageBreakElement}
          <hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;">
          <div>${header}</div>
          <br></br>
          <h1 class="ql-align-center">DUPLICATA PATIENT</h1>
          <div style="text-align:left;">${beforePrintContents}</div>
          <div>${correspondantsToPrint[i]}${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
          `
        }
        if (options.duplicata) {
          paginatedContent += `
          ${pageBreakElement}
          <hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;">
          <div>${header}</div>
          <br></br>
          <h1  class="ql-align-center">DUPLICATA</h1>
          <div style="text-align:left;">${beforePrintContents}</div>
          <div>${correspondantsToPrint[i]}${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
          `
        }
      } else {
        paginatedContent += `
          <div>${header}</div>
          <br></br>
          <div style="text-align:left;">${beforePrintContents}</div>
          <div>${correspondantsToPrint[i]}${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
          ${pageBreakElement}`;
        if (options.forPatient) {
          paginatedContent += `
          <hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;">
          <div>${header}</div>
          <br></br>
          <h1 class="ql-align-center">DUPLICATA PATIENT</h1>
          <div style="text-align:left;">${beforePrintContents}</div>
          <div>${correspondantsToPrint[i]}${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
          ${pageBreakElement}`;
        }
        if (options.duplicata) {
          paginatedContent += `
          <hr style="border: none; border-top: 2px solid #ccc; margin: 20px 0;">
          <div>${header}</div>
          <br></br>
          <h1 class="ql-align-center">DUPLICATA</h1>
          <div style="text-align:left;">${beforePrintContents}</div>
          <div>${correspondantsToPrint[i]}${content}</div>
          <div style="text-align:center;" class="ql-align-center">${footer}</div>
          ${pageBreakElement}`;
        }
      }
    }

    return `
      <html style="height: 100%;">
        <head>
          <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
          <style>
          ${QuillUtilsService.setQuillPreviewStyle(QuillUtilsService.resetStyle)}
          </style>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
        </head>
        <body style="height: 100%; margin: 0 13px;">
          <div id="editor" class="ql-container ql-bubble">
            <div style="height: 100%; display: flex; flex-direction: column;" class="ql-editor fr-view">
            ${paginatedContent}
            </div>
          </div>
        </body>
      </html>
    `;
  }

 /**
  *  gère l'impression d'un document sans correspondants, avec un header sur chaque page
  */
  private async _generateContentToPrint(content: string, firstPageOnly: boolean, beforePrintContents: string, enteteVide: boolean, header: string, headerHeight:number, footer: string, footerHeight:number, options: { forPatient: boolean, duplicata: boolean }, title?: string): Promise<string> {
  let enteteVideHeight = "0";

  headerHeight = await this.getOrCalculateHeaderHeight(header, headerHeight, firstPageOnly);
  footerHeight = await this.getOrCalculateFooterHeight(footer, footerHeight);

  if(enteteVide === true){
    enteteVideHeight = await this.getEnteteVideHeight();
  }

  if (firstPageOnly) {
    return this._generateContentToPrintFirstPageOnly(content, beforePrintContents, enteteVide, header, footer, footerHeight, options, title);
  } else {

  let customPrintStyle = `
   body {
      font-family: Arial, Helvetica, sans-serif;
  }
  table, th, td {
    border: 1px solid grey;
    border-collapse: collapse;
  }
  .entete {
      width: 100%;
      position: running(entete);
  }
  .entete img{
    max-width:100%;
  }

  .ql-editor {
      padding:0 !important;
  }
  .pied {
      width: 100%;
      position: running(pied);
  }


  .content h6,
  .content h5,
  .content h4 {
    margin: 0 !important;
  }
  .content h5, h4{
     font-weight: 400 !important;
  }
  .content p {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  .content .ql-align-center{
    text-align: center !important;
  }
  .content .ql-align-right{
    text-align: right !important;
  }
  .content .ql-align-left{
    text-align: left !important;
  }
  .content .before-print-content{
    margin-bottom:15px !important;
  }
   .content p, li {
      font-size: 13px !important;
    }
    .content h4, ul{
      font-size: 16px !important;
    }
    .content h6 {
      font-weight: normal !important;
    }
    .content h3 {
      font-size: 18px !important;
    }
    .content h2 {
      font-size: 20px !important;
    }
    .content h1 {
      font-size: 24px !important;
    }
.content {
  margin-top:20px;
  height: auto;
  display: block;
}
  .duplicata{
      page-break-before: always;
  }
  @page {
      size: A4;
      margin-left:10mm;
      margin-right:10mm;
      margin-top: ${!enteteVide? `${headerHeight + 10}px` : `${enteteVideHeight}cm`} !important;
      margin-bottom:${footerHeight}px !important;

      @top-center {
        padding-top:20px 0px;
        content: element(entete);
      }

     @bottom-center {
       content: element(pied);
      }
  }
    `;
      let html = `
      <html>
        <head>
          <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
          <style>
          ${QuillUtilsService.setQuillPreviewStyle(customPrintStyle)}
          </style>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
          <script src="https://unpkg.com/@webcomponents/webcomponentsjs@2.6.0/webcomponents-bundle.js"></script>
          <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>
       </head>
        <body class="fr-view">
          ${!enteteVide ? ` <div class="entete ql-editor">${header}</div>` : '<div class="entete"></div>'}
          <div class="pied ql-editor">${footer}</div>
          <div class="content">
           ${beforePrintContents ? `<div class="before-print-content">${beforePrintContents}</div>` : ''}
          ${content}
          </div>
        </body>
      </html>
      `;

      if(options.duplicata){
        html += `
          ${!enteteVide? ` <div class="entete ql-editor">${header}</div>`: '<div class="entete"></div>'}
            <div class="pied ql-editor">${footer}</div>
            <div  class="content duplicata">
              ${!enteteVide? '': `<div class="enteteVide"></div>`}
              <h1  class="ql-align-center">DUPLICATA</h1>
              ${beforePrintContents ? `<div class="before-print-content">${beforePrintContents}</div>` : ''}
              ${content}
            </div>
        `
      }
      if(options.forPatient){
        html += `
          ${!enteteVide? ` <div class="entete ql-editor">${header}</div>` : '<div class="entete"></div>'}
            <div class="pied ql-editor">${footer}</div>
            <div  class="content duplicata ">
              ${!enteteVide? '': `<div class="enteteVide"></div>`}
              <h1  class="ql-align-center">DUPLICATA PATIENT</h1>
              ${beforePrintContents ? `<div class="before-print-content">${beforePrintContents}</div>` : ''}
              ${content}
            </div>
        `
      }
      return html;
    }
    }

    /**
     * Gère l'impression d'un document avec un header sur la première page uniquement.
     */
  private async _generateContentToPrintFirstPageOnly(content: string, beforePrintContents: string, enteteVide: boolean, header: string, footer: string, footerHeight: number, options: { forPatient: boolean, duplicata: boolean }, title: string): Promise<string> {
    let enteteVideHeight = "0";
    footerHeight = await this.getOrCalculateFooterHeight(footer, footerHeight);

    if(enteteVide === true){
      enteteVideHeight = await this.getEnteteVideHeight();
    }

    let customPrintStyle = `
    body {
       font-family: Arial, Helvetica, sans-serif;
    }
    table, th, td {
      border: 1px solid grey;
      border-collapse: collapse;
    }
   .pied {
      width: 100%;
      position: running(pied);
    }
    .enteteVide{
      height: ${enteteVideHeight}cm !important;
    }
    .content h6,
    .content h5,
    .content h4 {
      margin: 0 !important;
    }
    .content h5, h4{
     font-weight: 400 !important;
    }
    .content p {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }
    .content .before-content{
      margin-top:20px;
    }
    .content  .ql-editor{
      padding: 0 !important;
    }
    .content .ql-editor img{
      max-width: 100%;
      margin-bottom: 10px;
    }
    .content .ql-align-center{
      text-align: center !important;
    }
    .content .ql-align-right{
      text-align: right !important;
    }
    .content .ql-align-left{
      text-align: left !important;
    }
    .content .corps{
      margin-top:20px;
    }
    .content {
      margin-top:10px;
      height: auto;
      display: block;
    }
     .content p, li {
      font-size: 13px !important;
    }
    .content h4, ul{
      font-size: 16px !important;
    }
    .content h6 {
      font-weight: normal !important;
    }
    .content h3 {
      font-size: 18px !important;
    }
    .content h2 {
      font-size: 20px !important;
    }
    .content h1 {
      font-size: 24px !important;
    }
    .duplicata{
      page-break-before: always;
    }
      @page {
        size: A4;
        margin-left:10mm;
        margin-right:10mm;
        margin-top:5mm;
        margin-bottom:${footerHeight}px !important;

        @bottom-center {
          content: element(pied);
        }
      }
   `;

      let html = `
      <html>
        <head>
          <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
          <style>
          ${QuillUtilsService.setQuillPreviewStyle(customPrintStyle)}
          </style>
           <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
          <script src="https://unpkg.com/@webcomponents/webcomponentsjs@2.6.0/webcomponents-bundle.js"></script>
          <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>
        </head>
        <body class="fr-view">
          <div class="pied ql-editor">${footer}</div>
          <div  class="content">
            ${!enteteVide ?` <div class="ql-editor">${header}</div>` : '<div class="enteteVide"></div>'}
            ${beforePrintContents ? `<div class="before-content">${beforePrintContents}</div>` : ''}
            <div class="corps"> ${content}</div>
          </div>
        </body>
      </html>
      `;

      if(options.duplicata){
        html += `
            <div class="pied ql-editor">${footer}</div>
            <div  class="content duplicata ">
              ${!enteteVide?  ` <div class="ql-editor">${header}</div>` : '<div class="enteteVide"></div>'}
              <h1  class="ql-align-center">DUPLICATA</h1>
              ${beforePrintContents ? `<div class="before-content">${beforePrintContents}</div>` : ''}
              <div class="corps"> ${content}</div>
            </div>
        `
      }
      if(options.forPatient){
        html += `
            <div class="pied ql-editor">${footer}</div>
            <div  class="content duplicata ">
              ${!enteteVide? ` <div class="ql-editor">${header}</div>` : '<div class="enteteVide"></div>'}
              <h1  class="ql-align-center">DUPLICATA PATIENT</h1>
              ${beforePrintContents ? `<div class="before-content">${beforePrintContents}</div>` : ''}
              <div class="corps"> ${content}</div>
            </div>
        `
      }

      return html;
    }

    /**
     * Gère l'impression d'un document avec des correspondants et un header sur la première page uniquement.
     */
  private async _generateMultiplePageContentToPrintFirstPageOnly(
    content: string,
    beforePrintContents: string,
    enteteVide: boolean,
    header: string,
    footer: string,
    footerHeight: number,
    options: { forPatient: boolean; duplicata: boolean },
    correspondantsToPrint?: string[],
    title?: string
  ): Promise<string> {
    let enteteVideHeight = "0";
    footerHeight = await this.getOrCalculateFooterHeight(footer, footerHeight);

    if(enteteVide === true){
      enteteVideHeight = await this.getEnteteVideHeight();
    }
  let customPrintStyle = `
   body {
    font-family: Arial, Helvetica, sans-serif;
   }
    table, th, td {
      border: 1px solid grey;
      border-collapse: collapse;
    }
    .pied {
      width: 100%;
      position: running(pied);
    }
    .enteteVide{
      height: ${enteteVideHeight}cm;
    }
    .content h6,
    .content h5,
    .content h4 {
      margin:0 !important;
    }
    .content h5, h4{
     font-weight: 400 !important;
    }
    .content p {
      margin-top: 0 !important;
      margin-bottom:0 !important;
    }
    .content .ql-align-center{
      text-align: center !important;
    }
    .content .ql-align-right{
      text-align: right !important;
    }
    .content .ql-align-left{
      text-align: left !important;
    }
    .content .before-content{
      margin-top:20px;
    }
    .content .ql-editor{
      padding: 0 !important;
    }
    .content .ql-editor img{
      max-width: 100%;
      margin-bottom: 10px;
    }
    .content .corps{
      margin-top:20px;
    }
    .content p, li {
      font-size: 13px !important;
    }
    .content h4, ul{
      font-size: 16px !important;
    }
    .content h3 {
      font-size: 18px !important;
    }
    .content h2 {
      font-size: 20px !important;
    }
    .content h1 {
      font-size: 24px !important;
    }
    .content h6 {
      font-weight: normal !important;
    }
    .content {
      margin-top:10px;
      height: auto;
      display: block;
    }
    .duplicata{
        page-break-before: always;
    }
    .correspondant{
        page-break-before:always;
        font-size:13px !important;
    }
    .first-correspondant{
        page-break-before:avoid;
    }

   @page {
      size: A4;
      margin-left: 10mm;
      margin-right: 10mm;
      margin-top: 5mm;
      margin-bottom:${footerHeight}px !important;

      @bottom-center {
        content: element(pied);
      }
    }
  `;

    let html = `
    <html>
      <head>
        <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
        <style>
            ${QuillUtilsService.setQuillPreviewStyle(customPrintStyle)}
        </style>
        <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
        <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
        <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
        <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
        <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
        <script src="https://unpkg.com/@webcomponents/webcomponentsjs@2.6.0/webcomponents-bundle.js"></script>
        <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>

      </head>
      <body class="fr-view">
    `;
    let isFirstCorrespondant = true;
    for (let correspondant of correspondantsToPrint) {
      html += `
          <div class="pied ql-editor">${footer}</div>
          <div class="content correspondant ${isFirstCorrespondant ? "first-correspondant" : ""}">
            ${!enteteVide ? ` <div class="ql-editor">${header}</div>` : '<div class="enteteVide"></div>'}
            ${beforePrintContents ? `<div class="before-content">${beforePrintContents}</div>` : ''}
            <div style="text-align:right;">${correspondant}</div>
            <div class="corps"> ${content}</div>
          </div>
      `;

     if (options.duplicata) {
         html += `
          <div class="pied ql-editor">${footer}</div>
          <div  class="content duplicata ">
            ${!enteteVide ? ` <div class="ql-editor">${header}</div>` : '<div class="enteteVide"></div>'}
            <h1  class="ql-align-center">DUPLICATA</h1>
            ${beforePrintContents ? `<div class="before-content">${beforePrintContents}</div>` : ''}
            <div style="text-align:right;">${correspondant}</div>
            <div class="corps"> ${content}</div>
          </div>
         `;
     }

     if (options.forPatient) {
         html += `
           <div class="pied ql-editor">${footer}</div>
           <div  class="content duplicata ">
              ${!enteteVide? ` <div class="ql-editor">${header}</div>`: '<div class="enteteVide"></div>'}
              <h1  class="ql-align-center">DUPLICATA PATIENT</h1>
              ${beforePrintContents ? `<div class="before-content">${beforePrintContents}</div>` : ''}
              <div style="text-align:right;">${correspondant}</div>
              <div class="corps"> ${content}</div>
           </div>
         `;
     }
     isFirstCorrespondant = false;
  }

  html += `</body></html>`;
  return html;
  }

/**
* Gère l'impression d'un document, avec des correspondants et un header sur chaque page.
*/
private async _generateMultiplePageContentToPrint(
  content: string,
  firstPageOnly: boolean,
  beforePrintContents: string,
  enteteVide: boolean,
  header: string,
  headerHeight: number,
  footer: string,
  footerHeight: number,
  correspondantsToPrint: string[],
  options: { forPatient: boolean; duplicata: boolean },
  title?: string
): Promise<string> {
  let enteteVideHeight = "0";
  headerHeight = await this.getOrCalculateHeaderHeight(header, headerHeight, firstPageOnly);
  footerHeight = await this.getOrCalculateFooterHeight(footer, footerHeight);

  if(enteteVide === true){
    enteteVideHeight = await this.getEnteteVideHeight();
  }

  if(firstPageOnly){
    return await this._generateMultiplePageContentToPrintFirstPageOnly(content, beforePrintContents, enteteVide, header, footer, footerHeight, options, correspondantsToPrint, title);
  }else{
    let customPrintStyle = `
    body {
      font-family: Arial, Helvetica, sans-serif;
    }
    table, th, td {
      border: 1px solid grey;
      border-collapse: collapse;
    }
  .entete {
      width: 100%;
      position: running(entete);
  }
  .entete img{
    max-width:100%;
  }
  .ql-editor {
      padding:0 !important;
  }
  .pied {
      width: 100%;
      position: running(pied);
  }

  .content h6,
  .content h5,
  .content h4 {
    margin:0 !important;
  }
  .content h5, h4{
    font-weight: 400 !important;
  }
 .content p {
    margin-top: 0 !important;
    margin-bottom:0 !important;
  }
  .content .ql-align-center{
    text-align: center !important;
  }
  .content .ql-align-right{
    text-align: right !important;
  }
  .content .ql-align-left{
    text-align: left !important;
  }
  .content p, li {
    font-size: 13px !important;
  }
  .content h4, ul{
      font-size: 16px !important;
  }
  .content h3 {
    font-size: 18px !important;
  }
  .content h2 {
    font-size: 20px !important;
  }
  .content h1 {
    font-size: 24px !important;
  }
  .content h6 {
    font-weight: normal !important;
  }

  .content {
    margin-top:20px;
    height: auto;
    display: block;
  }
  .duplicata{
      page-break-before: always;
  }
  .correspondant{
      page-break-before:always;
      font-size: 13px !important;
  }
  .first-correspondant{
      page-break-before:avoid;
  }

  @page {
      size: A4;
      margin-left: 10mm;
      margin-right: 10mm;
      margin-top: ${!enteteVide? `${headerHeight + 10}px` : `${enteteVideHeight}cm`} !important;
      margin-bottom:${footerHeight}px !important;

      @top-center {
        padding:20px 0;
        content: element(entete);
      }

      @bottom-center {
        content: element(pied);
        }
      }
`;

  let html = `
  <html>
    <head>
      <title>${SafeHtmlUtils.escapeHtml(title) || 'Impression courrier'}</title>
      <style>
          ${QuillUtilsService.setQuillPreviewStyle(customPrintStyle)}
      </style>
      <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_editor.pkgd.min.css" rel="stylesheet" type="text/css" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/2.8.4/css/froala_style.min.css" rel="stylesheet" type="text/css" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
      <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
      <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
      <script src="https://unpkg.com/@webcomponents/webcomponentsjs@2.6.0/webcomponents-bundle.js"></script>
      <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>

    </head>
    <body class="fr-view">
  `;
  let isFirstCorrespondant = true;
  for (let correspondant of correspondantsToPrint) {
    html += `
    ${!enteteVide ? ` <div class="entete ql-editor">${header}</div>` : '<div class="entete"></div>'}
        <div class="pied ql-editor">${footer}</div>
        <div class="content correspondant ${isFirstCorrespondant ? "first-correspondant" : ""}">
            ${beforePrintContents ? `<div>${beforePrintContents}</div>` : ''}
            <div style="text-align:right;">${correspondant}</div>
            ${content}
        </div>
    `;

   if (options.duplicata) {
       html += `
       ${!enteteVide? ` <div class="entete ql-editor">${header}</div>`: '<div class="entete"></div>'}
        <div class="pied ql-editor">${footer}</div>
        <div  class="content duplicata ">
          <h1  class="ql-align-center">DUPLICATA</h1>
          ${beforePrintContents ? `<div>${beforePrintContents}</div>` : ''}
          <div style="text-align:right;">${correspondant}</div>
          ${content}
        </div>
       `;
   }

   if (options.forPatient) {
       html += `
        ${!enteteVide? ` <div class="entete ql-editor">${header}</div>` : '<div class="entete"></div>'}
         <div class="pied ql-editor">${footer}</div>
         <div  class="content duplicata ">
            <h1  class="ql-align-center">DUPLICATA PATIENT</h1>
            ${beforePrintContents ? `<div>${beforePrintContents}</div>` : ''}
            <div style="text-align:right;">${correspondant}</div>
            ${content}
         </div>
       `;
    }
    isFirstCorrespondant = false;
    }

    html += `</body></html>`;

    return html;
    }
  }

  private async getOrCalculateHeaderHeight(header: string, headerHeight: number, firstPageOnly: boolean): Promise<number> {
    if (header != "" && header != null) {
      if (headerHeight === null || headerHeight === 0) {
        return await this.calculateAndRegisterHeights("header", header, firstPageOnly);
      }
      return headerHeight;
    } else {
      return 0;
    }
  }

  private async getOrCalculateFooterHeight(footer: string, footerHeight: number): Promise<number> {
    if(footer != "" && footer != null){
      if(footerHeight === null || footerHeight  === 0){
        return await this.calculateAndRegisterHeights("footer", footer, null);
        }
      return footerHeight;
    }else{
      return 0;
    }
  }

  private async getEnteteVideHeight(): Promise<string> {
    const currentProfilStr = localStorage.getItem('currentProfil');
    if (!currentProfilStr){
      console.error("Aucun profil trouvé (dans la méthode getEnteteVideHeight)");
      return "0";
    }

    try {
      const currentProfil = JSON.parse(currentProfilStr);
      const params = await this._resourceSrv.getResourceSettings(currentProfil.id, 'profil');
      const hauteurSetting = params?.find(p => p.settingKey === enTeteVideHauteur);
      return hauteurSetting?.settingValue || "0";
    } catch (e) {
      console.warn("Erreur récupération enteteVideHeight", e);
      return "0";
    }
  }

  private async calculateAndRegisterHeights(option: string, content: string, firstPageOnly?: boolean): Promise<number> {
    try {
      const style = `
      .ql-editor {
          margin-bottom: 0px !important;
          min-height: 0px !important;

      }
      `;

      let html = `
      <html>
      <style>
         ${QuillUtilsService.setQuillPreviewStyle(style)}
      </style>
      <head>
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.bubble.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.core.css" rel="stylesheet">
          <link href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.css" rel="stylesheet">
      </head>
      <body>
          <div id="quillHead" class="ql-editor">${content}</div>
      </body>
      </html>`;

      // élément temporaire caché
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.visibility = "hidden";
      tempDiv.style.display = "block";
      tempDiv.style.height = "auto";
      tempDiv.style.width = "794px"; // largeur d'un document A4
      tempDiv.innerHTML = html;

      document.body.appendChild(tempDiv);

      // Vérifier si le contenu est réellement vide
      const quillEditor = tempDiv.querySelector(".ql-editor");
      const isEmpty = !quillEditor || quillEditor.innerHTML.trim() === "" || quillEditor.innerHTML.trim() === "<p><br></p>";

      const contentHeight = isEmpty ? 0 : tempDiv.scrollHeight;

      document.body.removeChild(tempDiv);

      // Si le contenu est vide, on retourne 0 directement sans persister en base
      if (isEmpty) {
        return 0;
      }

      if (option === "footer") {
        const rawFooter = {
          texte: content,
          footerHeight: contentHeight
        };
        await this._headFootSrv.createFooter(rawFooter);
      }
      if (option === "header") {
        const rawHeader = {
          texte: content,
          firstPageOnly: firstPageOnly,
          headerHeight: contentHeight
        };
        await this._headFootSrv.createHeader(rawHeader);
      }
      return contentHeight;
    } catch (error) {
      console.error("Erreur lors du calcul de la hauteur  :", error);
      this._snackBarSrv.openSnackBar("Erreur lors du calcul de la hauteur de l'entête ou du pied de page");
      return 0;
    }
  }
}


export interface CourrierContent {
  fullContent: string;
  quillLessContent: string;
  printContent: string;
  isPagedActive?: boolean;

}
